import { ChangeDetectorRef, Component, NgZone, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material';
import {
	MachineCampus,
	MachineCampusResult,
	StripeReader,
	StripeReadersHttpService
} from '../../../../core/stripe-readers/_services/stripe-readers-http.service';
import { SettingsHttpService } from '../../../../core/settings/_services/settings-http.service';
import { MachineRouteService } from '../machine-route.service';

// Registration is the only way a reader gets onto a machine: confirm the machine's campus, then
// enter the code. The campus's Stripe location is found or created by the server.
type CardReaderMode = 'none' | 'confirm' | 'register' | 'assigned';

// Shape of `error` in the dashboard-server failure body for reader calls.
interface ReaderFailureDetails {
	stripe_reader_id?: string;
	registration_completed?: boolean;
	assignment_saved?: boolean;
	iot_updated?: boolean;
	assignment_removed?: boolean;
	deleted_in_stripe?: boolean;
}

interface StripeShadowFields {
	payment_provider?: string;
	stripe_sidecar_enabled?: boolean;
	stripe_location_id?: string;
	stripe_reader_id?: string;
}

// Compares what the portal asked the machine for (shadow desired) with what the machine
// says it is running (shadow reported).
interface MachineStatus {
	kind: 'in_use' | 'pending' | 'mismatch' | 'stripe_on' | 'off' | 'unavailable';
	title: string;
	detail: string;
}

@Component({
	selector: 'kt-card-reader',
	templateUrl: './card-reader.component.html',
	styleUrls: ['./card-reader.component.scss']
})
export class CardReaderComponent implements OnInit {
	machineId = '';
	machineName = '';

	mode: CardReaderMode = 'none';
	isLoading = false;
	isBusy = false;
	errorMessage = '';

	reader: StripeReader | null = null;
	machineSerial = '';
	machineStatus: MachineStatus | null = null;
	isStatusLoading = false;
	confirmRemove = false;
	confirmReregister = false;
	// Shows the success banner on the assigned view right after a registration.
	justRegistered = false;
	// A reader taken off this machine that Stripe failed to delete; it can't be registered again until it is.
	undeletedReaderId = '';

	// The campus a new reader is registered to, and the one the user confirmed.
	campusResult: MachineCampusResult | null = null;
	isCampusLoading = false;
	confirmedCampusId: number | null = null;

	registrationCode = '';

	constructor(
		private stripeReadersHttp: StripeReadersHttpService,
		private snackBar: MatSnackBar,
		private settingsHttp: SettingsHttpService,
		private ngZone: NgZone,
		private cdr: ChangeDetectorRef,
		private route: ActivatedRoute,
		private machineRouteService: MachineRouteService
	) {}

	ngOnInit(): void {
		const machine = this.machineRouteService.getMachine(this.route);
		if (!machine) {
			return;
		}
		this.machineId = String(machine.machine_id || '');
		this.machineName = String(machine.machine_name || '');
		this.machineSerial = String(machine.serial_no || '');
		this.loadCampus();
		this.loadReaders();
	}

	get campus(): MachineCampus | null {
		return this.campusResult ? this.campusResult.campus : null;
	}

	get campusName(): string {
		return (this.campus && this.campus.name) || 'this campus';
	}

	// Matches the label the server gives the reader in Stripe.
	get readerLabel(): string {
		const name = this.machineName.trim();
		return name && name !== this.machineId ? `${name} - ${this.machineId}` : this.machineId;
	}

	get campusAddressLines(): string[] {
		const address = this.campus && this.campus.address;
		if (!address) {
			return [];
		}
		const cityLine = [[address.city, address.state].filter(Boolean).join(', '), address.postal_code]
			.filter(Boolean)
			.join(' ');
		return [address.line1, address.line2, cityLine, address.country].filter(Boolean);
	}

	// Stripe cannot move a reader between locations, so one registered elsewhere has to be re-registered.
	get readerInWrongLocation(): boolean {
		const campusLocationId = this.reader && this.reader.assignment
			? this.reader.assignment.campus_stripe_location_id
			: '';
		return Boolean(
			this.reader &&
			this.reader.in_stripe !== false &&
			campusLocationId &&
			this.reader.location_id &&
			this.reader.location_id !== campusLocationId
		);
	}

	get statusIcon(): string {
		switch (this.machineStatus && this.machineStatus.kind) {
			case 'in_use':
				return 'check_circle';
			case 'pending':
				return 'schedule';
			case 'mismatch':
			case 'stripe_on':
				return 'error_outline';
			default:
				return 'help_outline';
		}
	}

	// onDone runs once the list has loaded (or failed), before the view updates.
	loadReaders(nextMode?: CardReaderMode, onDone?: () => void): void {
		this.isLoading = true;
		this.stripeReadersHttp.listReaders().subscribe({
			next: response => {
				const readers = response && response.data && Array.isArray(response.data.readers) ? response.data.readers : [];
				this.reader = readers.find(reader => Boolean(reader.assignment && reader.assignment.machine_id === this.machineId)) || null;
				const fallbackMode: CardReaderMode = this.reader ? 'assigned' : 'none';
				this.mode = nextMode && (nextMode !== 'assigned' || this.reader) ? nextMode : fallbackMode;
				this.isLoading = false;
				if (onDone) {
					onDone();
				}
				this.cdr.markForCheck();
				this.loadMachineStatus();
			},
			error: error => {
				this.isLoading = false;
				if (onDone) {
					onDone();
				}
				this.cdr.markForCheck();
				this.errorMessage = this.failureMessage(error, 'Failed to load Stripe card readers.');
			}
		});
	}

	loadCampus(): void {
		this.isCampusLoading = true;
		this.stripeReadersHttp.getMachineCampus(this.machineId).subscribe({
			next: response => {
				this.isCampusLoading = false;
				this.cdr.markForCheck();
				this.campusResult = response && response.data ? response.data : null;
				// A confirmation only counts for the campus that was shown.
				if (!this.campus || this.campus.campus_id !== this.confirmedCampusId) {
					this.confirmedCampusId = null;
					if (this.mode === 'register') {
						this.mode = 'confirm';
					}
				}
			},
			error: error => {
				this.isCampusLoading = false;
				this.cdr.markForCheck();
				this.errorMessage = this.failureMessage(error, 'Failed to load the machine’s campus.');
			}
		});
	}

	// Asks the user to confirm the campus, with fresh data in case it was just changed elsewhere.
	startRegistration(): void {
		this.resetForms();
		this.confirmedCampusId = null;
		this.mode = 'confirm';
		this.loadCampus();
	}

	confirmCampus(): void {
		if (!this.campus || !this.campusResult || !this.campusResult.address_complete) {
			return;
		}
		this.confirmedCampusId = this.campus.campus_id;
		this.mode = 'register';
	}

	cancel(): void {
		this.resetForms();
		this.confirmedCampusId = null;
		this.mode = this.reader ? 'assigned' : 'none';
	}

	registerReader(): void {
		const registrationCode = this.normalizedRegistrationCode();
		if (!registrationCode) {
			this.errorMessage = 'Enter the registration code shown on the reader.';
			return;
		}
		if (!this.confirmedCampusId) {
			this.mode = 'confirm';
			return;
		}
		this.isBusy = true;
		this.errorMessage = '';
		this.stripeReadersHttp
			.registerReader({
				registration_code: registrationCode,
				machine_id: this.machineId,
				confirmed_campus_id: this.confirmedCampusId
			})
			.subscribe({
				// Stay on the busy form until the reader list has the new reader, then switch views
				// in one step; clearing the code earlier showed an empty form for a moment.
				next: () => this.loadReaders('assigned', () => {
					this.finishRegistrationForm();
					this.justRegistered = true;
				}),
				error: error => {
					const details = this.failureDetails(error);
					this.errorMessage = this.failureMessage(error, 'Failed to register the card reader.');
					if (details.assignment_saved) {
						this.loadReaders('assigned', () => this.finishRegistrationForm());
						return;
					}
					this.isBusy = false;
					this.cdr.markForCheck();
					if (!details.registration_completed) {
						// The campus may have changed since it was confirmed; this sends the user back to confirm it.
						this.loadCampus();
					}
					// Registered but not assigned: the form keeps the same code, and retrying reuses the same reader.
				}
			});
	}

	retrySync(): void {
		if (!this.reader) {
			return;
		}
		this.justRegistered = false;
		this.isBusy = true;
		this.errorMessage = '';
		this.stripeReadersHttp.assignReader(this.reader.stripe_reader_id, { machine_id: this.machineId }).subscribe({
			next: () => {
				this.isBusy = false;
				this.cdr.markForCheck();
				this.snackBar.open('Settings sent to the machine.', '', { duration: 5000 });
				this.loadReaders('assigned');
			},
			error: error => {
				this.isBusy = false;
				this.cdr.markForCheck();
				this.errorMessage = this.failureMessage(error, 'Failed to send settings to the machine.');
			}
		});
	}

	// Takes the reader off this machine and deletes it in Stripe, so it can be registered again anywhere.
	removeReader(): void {
		if (this.reader) {
			this.deleteReader(
				this.reader.stripe_reader_id,
				'Reader removed and deleted from Stripe. The machine will restart in about a minute with card payments off.',
				'none'
			);
		}
	}

	// Stripe can't move a reader between locations; deleting it lets it be registered to this campus.
	reregisterReader(): void {
		if (!this.reader) {
			return;
		}
		this.deleteReader(
			this.reader.stripe_reader_id,
			'Reader deleted from Stripe. The machine will restart in about a minute. Confirm the campus, then enter a new code from the reader.',
			'confirm'
		);
	}

	retryDelete(): void {
		if (this.undeletedReaderId) {
			this.deleteReader(this.undeletedReaderId, 'Reader deleted from Stripe.', this.reader ? 'assigned' : 'none');
		}
	}

	loadMachineStatus(): void {
		if (!this.machineSerial) {
			this.machineStatus = {
				kind: 'unavailable',
				title: 'Machine settings unavailable',
				detail: 'This machine has no serial number, so it cannot receive card reader settings.'
			};
			return;
		}
		this.isStatusLoading = true;
		// getShadowData resolves an Amplify promise outside Angular's zone; re-enter it so the view updates.
		this.settingsHttp.getShadowData(this.machineSerial).subscribe({
			next: shadow => this.ngZone.run(() => {
				this.isStatusLoading = false;
				this.cdr.markForCheck();
				let doc: any = null;
				try {
					doc = shadow && shadow.payload ? JSON.parse(shadow.payload) : null;
				} catch (e) {
					doc = null;
				}
				this.machineStatus = this.describeMachineStatus(doc);
			}),
			error: () => this.ngZone.run(() => {
				this.isStatusLoading = false;
				this.cdr.markForCheck();
				this.machineStatus = {
					kind: 'unavailable',
					title: 'Machine settings unavailable',
					detail: 'The machine’s IoT settings could not be read. It may not be set up in AWS IoT yet.'
				};
			})
		});
	}

	readerName(reader: StripeReader | null): string {
		return reader ? reader.label || reader.serial_number || reader.stripe_reader_id : '';
	}

	formatDeviceType(deviceType: string): string {
		return String(deviceType || 'Card reader')
			.split('_')
			.map(part => part.toUpperCase() === part ? part : part.charAt(0).toUpperCase() + part.slice(1))
			.join(' ');
	}

	isOnline(reader: StripeReader): boolean {
		return String(reader && reader.status).toLowerCase() === 'online';
	}

	private deleteReader(stripeReaderId: string, successMessage: string, nextMode: CardReaderMode): void {
		this.justRegistered = false;
		this.isBusy = true;
		this.errorMessage = '';
		this.stripeReadersHttp.deregisterReader(stripeReaderId).subscribe({
			next: () => {
				this.isBusy = false;
				this.cdr.markForCheck();
				this.confirmRemove = false;
				this.confirmReregister = false;
				this.undeletedReaderId = '';
				this.snackBar.open(successMessage, '', { duration: 6000 });
				if (nextMode === 'confirm') {
					this.loadCampus();
				}
				this.loadReaders(nextMode);
			},
			error: error => {
				this.isBusy = false;
				this.cdr.markForCheck();
				this.confirmRemove = false;
				this.confirmReregister = false;
				const details = this.failureDetails(error);
				this.errorMessage = this.failureMessage(error, 'Failed to delete the reader from Stripe.');
				this.undeletedReaderId = details.assignment_removed && !details.deleted_in_stripe ? stripeReaderId : '';
				this.loadReaders();
			}
		});
	}

	private describeMachineStatus(doc: any): MachineStatus {
		const state = (doc && doc.state) || {};
		const desired: StripeShadowFields = state.desired || {};
		const reported: StripeShadowFields = state.reported || {};
		const reportedMeta = (doc && doc.metadata && doc.metadata.reported) || {};
		const reportedAt = reportedMeta.stripe_reader_id && reportedMeta.stripe_reader_id.timestamp
			? new Date(reportedMeta.stripe_reader_id.timestamp * 1000).toLocaleString()
			: '';
		const usesStripe = (fields: StripeShadowFields, readerId?: string) =>
			fields.payment_provider === 'stripe' &&
			fields.stripe_sidecar_enabled === true &&
			Boolean(fields.stripe_reader_id) &&
			(!readerId || fields.stripe_reader_id === readerId);

		if (!this.reader) {
			if (usesStripe(reported) || usesStripe(desired)) {
				const readerId = reported.stripe_reader_id || desired.stripe_reader_id;
				return {
					kind: 'stripe_on',
					title: 'Card payments are still on',
					detail: `No reader is assigned here, but the machine is set to use reader ${readerId}.`
				};
			}
			return { kind: 'off', title: 'Card payments are off', detail: '' };
		}

		const readerId = this.reader.stripe_reader_id;
		if (usesStripe(reported, readerId)) {
			return {
				kind: 'in_use',
				title: 'Machine is using this reader',
				// AWS stamps a reported field only when the machine writes it, which can predate this assignment
				// when the machine already had these settings.
				detail: reportedAt ? `The machine last reported this reader on ${reportedAt}.` : ''
			};
		}
		if (usesStripe(desired, readerId)) {
			const otherReader = reported.stripe_reader_id && reported.stripe_reader_id !== readerId
				? ` It still reports reader ${reported.stripe_reader_id}.`
				: '';
			return {
				kind: 'pending',
				title: 'Waiting for the machine to apply the settings',
				// The machine restarts about a minute after it gets new card reader settings, so this is normal at first.
				detail: `The settings were sent, but the machine has not confirmed them.${otherReader} It restarts to apply them, which takes a few minutes. If this lasts longer, it may be offline.`
			};
		}
		const configuredReader = desired.stripe_reader_id
			? ` It is set to reader ${desired.stripe_reader_id}.`
			: '';
		const nextStep = this.reader.in_stripe === false
			? ''
			: ' Re-send the settings so the machine uses this reader.';
		return {
			kind: 'mismatch',
			title: 'Machine settings don’t match this reader',
			detail: `${configuredReader}${nextStep}`.trim()
		};
	}

	// Codes are lowercase words joined by hyphens; fix the usual typing slips (spaces, capitals) rather
	// than rejecting them. Stripe reports a code that is actually wrong.
	private normalizedRegistrationCode(): string {
		return this.registrationCode.trim().toLowerCase().replace(/\s*-\s*|\s+/g, '-');
	}

	private finishRegistrationForm(): void {
		this.isBusy = false;
		this.registrationCode = '';
		this.confirmedCampusId = null;
	}

	private resetForms(): void {
		this.errorMessage = '';
		this.justRegistered = false;
		this.confirmRemove = false;
		this.confirmReregister = false;
		this.registrationCode = '';
	}

	private failureMessage(error: any, fallback: string): string {
		const data = error && error.response && error.response.data;
		return (data && data.message) || (error && error.message) || fallback;
	}

	private failureDetails(error: any): ReaderFailureDetails {
		const data = error && error.response && error.response.data;
		return (data && data.error && typeof data.error === 'object') ? data.error : {};
	}
}
